import { Break, Work } from './Work';
import { DateUtilsWithTimezone } from '../utils/DateUtils/DateUtils';

export class WorkUtils {
  constructor(private readonly dateUtils: DateUtilsWithTimezone) {}

  /**
   * Returns the duration of a work unit in minutes.
   * This is the timespan between the start & end date of work,
   * minus all breaks.
   *
   * @example
   * const duration = getWorkDurationInMin({
   *   start: new Date('2022-01-31T10:00:00.000Z'),
   *   end: new Date('2022-01-31T17:00:00.000Z'),
   *   breaks: [
   *     {
   *       start: new Date('2022-01-31T12:00:00.000Z'),
   *       end: new Date('2022-01-31T13:00:00.000Z')
   *     }
   *   ]
   * });
   * assert(duration === 6 * 60);
   */
  public getWorkDurationInMin(work: Work): number {
    const workDuration = this.dateUtils.getDiffInMinutes(work.start, work.end);
    const breaksDuration = (work.breaks ?? [])
      // Filter out breaks that fully lie outside work
      .filter((b) => {
        const isBefore =
          b.start.getTime() <= work.start.getTime() &&
          b.end.getTime() <= work.start.getTime();
        const isAfter =
          b.start.getTime() >= work.end.getTime() &&
          b.end.getTime() >= work.end.getTime();
        return !(isBefore || isAfter);
      })
      // Limit breaks that partially lie outside work to work start & end
      .map(
        (b) =>
          ({
            start: this.dateUtils.max([b.start, work.start]),
            end: this.dateUtils.min([b.end, work.end])
          }) as Break
      )
      .map((b) => this.getBreakDurationInMin(b))
      .reduce((prev, cur) => prev + cur, 0);
    return workDuration - breaksDuration;
  }

  /**
   * Returns the duration of a break in minutes.
   *
   * @example
   * const duration = getBreakDurationInMin({
   *   start: new Date('2022-01-31T12:00:00.000Z'),
   *   end: new Date('2022-01-31T16:00:00.000Z')
   * });
   * assert(duration === 4 * 60);
   */
  public getBreakDurationInMin(brk: Break): number {
    return this.dateUtils.getDiffInMinutes(brk.start, brk.end);
  }
}
